import { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { trimObjectStrings } from 'utils'
import { fetch, deleteVariation, reset, updateVariations } from '../../../actions'
import { useSelectedFleetIds } from 'features/settings'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'

import VariationDetails from './variation-details'
import VariationRequestsTable from './variations-requests-table'

const Variations = ({
  id,
  data,
  flags,
  errorMessage,
  userRoles,
  onFetch,
  onUpdateVariations,
  onDeleteVariation,
  onReset,
}) => {
  const fleetIds = useSelectedFleetIds()

  const [loading, setLoading] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)

  useEffect(() => {
    setLoading(true)
    onReset()
    onFetch(data.agreementNo, fleetIds)
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    if (flags.deleteVariation === 'processed') {
      onFetch(data.agreementNo, fleetIds)
    }
  }, [flags.deleteVariation]) // eslint-disable-line

  useEffect(() => {
    if (loading && flags.fetch === 'processed') {
      setLoading(false)
    }
  }, [flags.fetch]) // eslint-disable-line

  const maturityDateExpired = !!data.maturityDate && dayjs(data.maturityDate).isBefore(dayjs())

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card sx={{ boxShadow: 'none', borderRadius: '0 0 5px 5px' }}>
        <CardContent sx={{ p: 3 }}>
          <Stack gap={2}>
            <Stack gap={2}>
              <Typography variant="h5" fontWeight={600}>
                Existing Contract
              </Typography>

              <VariationDetails
                maturityDateExpired={maturityDateExpired}
                items={[
                  { label: 'Agreement Type', value: data.agreementType ?? '-' },
                  { label: 'Term', value: data.term ?? '-' },
                  { label: 'Contract KM\'s', value: data.contractKm ? parseInt(data.contractKm, 10).toLocaleString('en-NZ') : '-' },
                  { label: 'Maturity Date', value: data.maturityDate ? dayjs(data.maturityDate).format('Do MMMM YYYY') : '-' },
                  { label: 'Tyres', value: data.noOfTyres ?? '-' },
                ]}/>
            </Stack>

            <Stack gap={2}>
              <Typography variant="h5" fontWeight={600}>
                Variation Requests
              </Typography>

              <VariationRequestsTable
                isLoading={loading}
                readOnly={maturityDateExpired}
                data={data}
                flags={{ ...flags, updatingStatus }}
                errorMessage={errorMessage}
                userRoles={userRoles}
                onUpdate={onUpdateVariations}
                onDeleteVariation={onDeleteVariation}
                setUpdatingStatus={setUpdatingStatus}/>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </LocalizationProvider>
  )
}

Variations.propTypes = {
  id: PropTypes.string,
  data: PropTypes.object.isRequired,
  flags: PropTypes.object.isRequired,
  errorMessage: PropTypes.string.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  onFetch: PropTypes.func.isRequired,
  onUpdateVariations: PropTypes.func.isRequired,
  onDeleteVariation: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
}

const mapStateToProps = ({ fleet, settings }, ownProps) => ({
  data: trimObjectStrings(fleet.data?.[ownProps.id]),
  flags: fleet.flags,
  errorMessage: fleet.errorMessage || '',
  userRoles: settings.roles,
})

const mapDispatchToProps = (dispatch) => ({
  onFetch: (agreementNo, fleetId) =>
    dispatch(fetch(null, { resourceId: agreementNo, queryParams: { fleetId } })),

  onUpdateVariations: (payload) => dispatch(updateVariations(payload)),

  onDeleteVariation: (id, fleetId) =>
    dispatch(deleteVariation(null, { resourceId: id, queryParams: { fleetId } })),

  onReset: () => dispatch(reset()),
})

const Connected = connect(mapStateToProps, mapDispatchToProps)(Variations)

export default function VariationsWithParams(props) {
  const { id } = useParams()
  return <Connected {...props} id={id} />
}
